import React, { useMemo, useState } from 'react';
import { Check, ChevronDown, ChevronRight, Info, Search, TriangleAlert } from 'lucide-react';
import { RegulationLink } from '../ui/RegulationLink';
type AdvancedDetail = {
  label: string;
  value: string;
};
type SubIngredient = {
  id: string;
  name: string;
  role: 'Active' | 'Excipient';
  resolved?: string;
  source?: string;
  advanced?: AdvancedDetail[];
};
type FoodRow = {
  id: string;
  name: string;
  tag?: string;
  role?: 'Active' | 'Excipient';
  resolved?: string;
  amount?: string;
  source?: string;
  compound?: boolean;
  subIngredients?: SubIngredient[];
};
type FoodGroup = {
  title: string;
  ratio: string;
  rows: FoodRow[];
};
const foodStandardLine = 'Regulated under food-level standards. No additive limits apply.';
const additiveSource = 'FSANZ Schedule 15 — additive absent for this category';
const filters = ['All', 'Restricted', 'Prohibited', 'Food ingredient (OK) 4', 'Not in Code schedules', 'Not listable', 'Unknown'];
const groups: FoodGroup[] = [{
  title: 'Compound ingredients',
  ratio: '3 / 3',
  rows: [{
    id: 'choc-malt-blend',
    name: 'Choc Malt Blend',
    compound: true,
    subIngredients: [{
      id: 'soy',
      name: 'Soy',
      role: 'Excipient',
      resolved: 'soya'
    }]
  }, {
    id: 'minerals',
    name: 'Minerals',
    compound: true,
    subIngredients: [{
      id: 'calcium',
      name: 'Calcium',
      role: 'Active',
      advanced: [{
        label: 'Resolution',
        value: 'Exact name match'
      }, {
        label: 'Processing aid',
        value: 'calcium pantothenate (generally permitted)'
      }]
    }, {
      id: 'iron',
      name: 'Iron',
      role: 'Active',
      source: additiveSource,
      advanced: [{
        label: 'Resolution',
        value: 'Exact name match'
      }, {
        label: 'Source',
        value: additiveSource
      }, {
        label: 'Processing aid',
        value: 'Iron oxide (generally permitted)'
      }]
    }]
  }, {
    id: 'vitamins',
    name: 'Vitamins',
    compound: true,
    subIngredients: [{
      id: 'vit-c',
      name: 'C',
      role: 'Active',
      resolved: 'ascorbic acid'
    }, {
      id: 'vit-b3',
      name: 'B3',
      role: 'Active',
      resolved: 'nicotinamide'
    }, {
      id: 'vit-b6',
      name: 'B6',
      role: 'Active',
      resolved: 'pyridoxine hydrochloride'
    }, {
      id: 'vit-b2',
      name: 'B2',
      role: 'Active',
      resolved: 'riboflavin'
    }, {
      id: 'vit-d',
      name: 'D',
      role: 'Active',
      resolved: 'cholecalciferol'
    }, {
      id: 'vit-b12',
      name: 'B12',
      role: 'Active',
      resolved: 'cyanocobalamin'
    }]
  }]
}, {
  title: 'Food ingredients',
  ratio: '2 / 2',
  rows: [{
    id: 'malt-extract',
    name: 'Extract of Malt Barley or Malt Barley and Rice',
    tag: 'food_base',
    resolved: 'BARLEY MALT SYRUP',
    amount: '38%'
  }, {
    id: 'milk-solids',
    name: 'Milk Solids',
    tag: 'food_base',
    resolved: 'SKIM MILK'
  }]
}, {
  title: 'Additives',
  ratio: '1 / 1',
  rows: [{
    id: 'cocoa',
    name: 'Cocoa',
    tag: 'food_base',
    resolved: 'cocoa extract',
    source: additiveSource
  }]
}, {
  title: 'Processing aids',
  ratio: '1 / 1',
  rows: [{
    id: 'sugar',
    name: 'Sugar',
    role: 'Excipient',
    resolved: 'sucrose',
    source: additiveSource
  }]
}, {
  title: 'Flavourings',
  ratio: '0 / 0',
  rows: []
}, {
  title: 'Nutrients',
  ratio: '0 / 0',
  rows: []
}, {
  title: 'Probiotics',
  ratio: '0 / 0',
  rows: []
}, {
  title: 'Unknown',
  ratio: '0 / 0',
  rows: []
}];
export function FoodFormulaTab() {
  const [activeFilter, setActiveFilter] = useState('All');
  const [query, setQuery] = useState('');
  const [openCompounds, setOpenCompounds] = useState<string[]>([]);
  const [openSubs, setOpenSubs] = useState<string[]>([]);
  const [openAdvanced, setOpenAdvanced] = useState<string[]>([]);
  const toggle = (list: string[], setList: (value: string[]) => void, id: string) => setList(list.includes(id) ? list.filter(entry => entry !== id) : [...list, id]);
  const visibleGroups = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return groups;
    return groups.map(group => ({
      ...group,
      rows: group.rows.filter(row => `${row.name} ${row.resolved ?? ''}`.toLowerCase().includes(term))
    })).filter(group => group.rows.length > 0);
  }, [query]);
  return <div className="space-y-5" data-id="element-186" data-mp="18g6tulwyhwy1">
      <article className="rounded-2xl border-2 border-amber-300 border-l-4 border-l-amber-500 bg-amber-50 p-5 shadow-sm" data-id="element-187" data-mp="18k4e5av9suji">
        <div className="flex gap-3" data-id="element-188" data-mp="yk3shi9xi2c5">
          <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" data-id="element-189" data-mp="yl53xnuuwque" />
          <div data-id="element-190" data-mp="13hvmchu4vyiz">
            <h2 className="font-[Figtree] text-base font-semibold text-amber-950" data-id="element-191" data-mp="13jgey1ww17e7">Lower NPSC score from 15 to ≤ 4 to unlock health claims</h2>
            <p className="mt-2 text-sm leading-relaxed text-amber-900" data-id="element-192" data-mp="2xwpr61gtj705">
              The NPSC final score is 15, which is above the pass threshold of 4.0 for this category. The product cannot make health or nutrition content claims while the score exceeds this threshold. The score is driven by 19 baseline points, offset by 4 fibre points. To make the product eligible for claims, reformulate to reduce the final score to 4.0 or below by reducing energy, saturated fat, sodium or sugar content.
            </p>
            <RegulationLink text="see the linked verdict for the regulation reference" className="mt-3" data-id="element-193" data-mp="14zkgz6w2g7ci" />
          </div>
        </div>
      </article>

      <section className="rounded-2xl border border-[#dce3da] bg-white shadow-sm" aria-labelledby="food-verdicts-heading" data-id="element-194" data-mp="10xpehz2efnx5">
        <div className="border-b border-[#e5ebe3] px-6 py-5" data-id="element-195" data-mp="1evnsh3ct5ic4">
          <h2 id="food-verdicts-heading" className="inline-flex items-center gap-2 font-[Figtree] text-lg font-semibold text-evergreen" data-id="element-196" data-mp="1a08fedmmjqgr">
            <Info className="h-4 w-4 text-[#738478]" data-id="element-197" data-mp="3gr7x5b0idcl3" />
            Ingredient verdicts
          </h2>
          <p className="mt-2 font-mono text-xs font-semibold uppercase tracking-[0.11em] text-[#6f8174]" data-id="element-198" data-mp="3bsmxbcp64ux6">
            0 permitted · 0 with conditions · 0 not permitted · 4 food · 0 verification required{' '}
            <span className="text-amber-600" data-id="element-199" data-mp="1ontuxuna14dh">●</span> 2 LLM override(s)
          </p>

          <div className="mt-4 rounded-xl border border-[#e1e8df] bg-[#fbfdf9] px-4 py-3" data-id="element-200" data-mp="1oq6kasvd6h0k">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#748477]" data-id="element-201" data-mp="1tku105k4m74l">Ingredient name compliance</p>
            <ul className="mt-2 space-y-1.5" data-id="element-202" data-mp="23kkuq50v4i3m">
              {['Milk Solids — OK', 'Sugar — OK'].map(entry => <li key={entry} className="inline-flex w-full items-center gap-2 text-sm text-[#56685c]" data-id="element-203" data-mp="28c2tcmfey6lf">
                  <Check className="h-4 w-4 shrink-0 text-forest-green" data-id="element-204" data-mp="2cdxvft564o09" />
                  {entry}
                </li>)}
            </ul>
          </div>
        </div>

        <div className="px-6 py-5" data-id="element-205" data-mp="2haoacd58ldk3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between" data-id="element-206" data-mp="2mastkaxj4me0">
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter ingredients" data-id="element-207" data-mp="2wg28dj3q7ope">
              {filters.map(filter => <button key={filter} type="button" onClick={() => setActiveFilter(filter)} aria-pressed={activeFilter === filter} className={`rounded-full border px-2.5 py-1 text-xs font-semibold transition ${activeFilter === filter ? 'border-forest-green bg-forest-green text-white' : 'border-[#d8e1d5] bg-white text-[#56685c] hover:bg-[#f3f7f1]'}`} data-id="element-208" data-mp="2rfo8wpg6xn2z">
                  {filter}
                </button>)}
            </div>
            <label className="relative shrink-0" data-id="element-209" data-mp="3a7pf6cvaofmf">
              <span className="sr-only" data-id="element-210" data-mp="35fp7tzcwakft">Search ingredients</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#93a095]" data-id="element-211" data-mp="35dumwl1misbq" />
              <input type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search ingredients…" className="w-full rounded-lg border border-[#d8e1d5] bg-white py-2 pl-9 pr-3 text-sm text-evergreen placeholder:text-[#9aa79c] focus:border-forest-green focus:outline-none focus:ring-1 focus:ring-forest-green lg:w-64" data-id="element-212" data-mp="1qvp0rzibv2b" />
            </label>
          </div>

          <div className="mt-4 space-y-4" data-id="element-213" data-mp="lgz6yup5lfkz">
            {visibleGroups.map(group => <div key={group.title} className="overflow-hidden rounded-xl border border-[#e1e8df]" data-id="element-214" data-mp="6s355fdowyst">
                <div className="flex items-center justify-between bg-[#f4f7f2] px-4 py-2.5" data-id="element-215" data-mp="bpmga6f3cvgj">
                  <h3 className="font-[Figtree] text-sm font-semibold text-evergreen" data-id="element-216" data-mp="zfp6si3cxtd1">{group.title}</h3>
                  <span className="font-mono text-xs text-[#8b968d]" data-id="element-217" data-mp="14d8v6atrrmip">{group.ratio}</span>
                </div>
                {group.rows.length > 0 && <ul className="divide-y divide-[#edf0eb]" data-id="element-218" data-mp="ufknkkb2ekj4">
                    {group.rows.map(row => row.compound ? <li key={row.id} data-id="element-219" data-mp="3a78k4em62b1e">
                          <button type="button" onClick={() => toggle(openCompounds, setOpenCompounds, row.id)} aria-expanded={openCompounds.includes(row.id)} className="flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-[#f9fbf8]" data-id="element-220" data-mp="3a679uccz5skq">
                            <div className="min-w-0 flex-1" data-id="element-221" data-mp="1s9lyuw1vwrd3">
                              <p className="text-sm font-semibold text-evergreen" data-id="element-222" data-mp="1s93vlfgt1cvo">{row.name}</p>
                              <p className="mt-0.5 text-xs text-[#8b968d]" data-id="element-223" data-mp="268bvyboper1g">Sub-ingredients: {row.subIngredients?.length ?? 0}</p>
                            </div>
                            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800" data-id="element-224" data-mp="1eh5tvppfaj9j">
                              <div className="h-3.5 w-3.5" data-id="element-225" data-mp="19h1c7wcs3ihq" />
                              Compound ingredient
                            </span>
                            {openCompounds.includes(row.id) ? <ChevronDown className="h-4 w-4 shrink-0 text-[#91a093]" data-id="element-226" data-mp="1ii93vvkshl2u" /> : <ChevronRight className="h-4 w-4 shrink-0 text-[#91a093]" data-id="element-227" data-mp="uswo2cuyierk" />}
                          </button>
                          {openCompounds.includes(row.id) && <div className="border-t border-[#edf0eb] bg-[#fbfdf9] px-4 py-3" data-id="element-228" data-mp="23qce536cycs5">
                              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#748477]" data-id="element-229" data-mp="23nykmyrbt8mp">Sub-ingredients</p>
                              <ul className="mt-2 divide-y divide-[#e8ede6]" data-id="element-230" data-mp="1uol980jn69zc">
                                {row.subIngredients?.map(sub => <li key={sub.id} data-id="element-231" data-mp="1pprte1gvu6w0">
                                    <button type="button" onClick={() => toggle(openSubs, setOpenSubs, sub.id)} aria-expanded={openSubs.includes(sub.id)} className="flex w-full items-start gap-3 py-3 text-left" data-id="element-232" data-mp="1krgl3v68k4fw">
                                      <div className="min-w-0 flex-1" data-id="element-233" data-mp="1b15ge6eq9r7y">
                                        <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-evergreen" data-id="element-234" data-mp="1azasqkc2nl06">
                                          {sub.name}
                                          <span className="rounded-full bg-[#eef1ec] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-[#8b968d]" data-id="element-235" data-mp="120783bx9axjk">{sub.role}</span>
                                          {sub.resolved && <span className="font-normal text-[#718174]" data-id="element-236" data-mp="38ozl6p6gi3yy">
                                              <span className="text-[#a6b0a7]" data-id="element-237" data-mp="3dmrcpi36vo2m">→</span> {sub.resolved}
                                            </span>}
                                        </p>
                                        <p className="mt-1 text-sm text-[#617366]" data-id="element-238" data-mp="3moh26idigcxr">{foodStandardLine}</p>
                                        {sub.source && <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-[#8b968d]" data-id="element-239" data-mp="2pwn8p9m39mb3">Source: <RegulationLink text={sub.source} data-id="element-240" data-mp="2uy2td0kctqsy" /></p>}
                                      </div>
                                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-forest-green/10 px-2.5 py-1 text-xs font-semibold text-forest-green" data-id="element-241" data-mp="2yxlbxpopdpsm">
                                        <Check className="h-3.5 w-3.5" data-id="element-242" data-mp="2765t86q9on7o" />
                                        OK — food ingredient
                                      </span>
                                      {openSubs.includes(sub.id) ? <ChevronDown className="h-4 w-4 shrink-0 text-[#91a093]" data-id="element-243" data-mp="3eiogzq4pd1d3" /> : <ChevronRight className="h-4 w-4 shrink-0 text-[#91a093]" data-id="element-244" data-mp="39kv98snr3zap" />}
                                    </button>
                                    {openSubs.includes(sub.id) && sub.advanced && <div className="mb-3 rounded-lg border border-[#e1e8df] bg-white px-3 py-2.5" data-id="element-245" data-mp="3jciugbtpsk3q">
                                        <button type="button" onClick={() => toggle(openAdvanced, setOpenAdvanced, sub.id)} aria-expanded={openAdvanced.includes(sub.id)} className="inline-flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-[#748477]" data-id="element-246" data-mp="2vmoma7kcbjpt">
                                          {openAdvanced.includes(sub.id) ? <ChevronDown className="h-3.5 w-3.5" data-id="element-247" data-mp="30n2q1dews7i1" /> : <ChevronRight className="h-3.5 w-3.5" data-id="element-248" data-mp="jvkwm8jm6b6v" />}
                                          Advanced details
                                        </button>
                                        {openAdvanced.includes(sub.id) && <dl className="mt-2 space-y-1.5" data-id="element-249" data-mp="ndh1bv0rkcql">
                                            {sub.advanced.map(detail => <div key={detail.label} className="flex flex-wrap gap-x-2 text-sm" data-id="element-250" data-mp="sexzhirv5l3h">
                                                <dt className="text-[#8b968d]" data-id="element-251" data-mp="dhx85gdl3s5j">{detail.label}:</dt>
                                                <dd className="text-[#56685c]" data-id="element-252" data-mp="iffdkwse7p8s">{detail.value}</dd>
                                              </div>)}
                                          </dl>}
                                      </div>}
                                  </li>)}
                              </ul>
                            </div>}
                        </li> : <li key={row.id} className="flex flex-wrap items-start gap-3 px-4 py-3" data-id="element-253" data-mp="11djbev2ty1va">
                          <div className="min-w-0 flex-1" data-id="element-254" data-mp="1p33vxiemt76o">
                            <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-evergreen" data-id="element-255" data-mp="1p1j4i1nm64d1">
                              {row.name}
                              {(row.tag || row.role) && <span className="rounded-full bg-[#eef1ec] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-[#8b968d]" data-id="element-256" data-mp="67lnxz55gb18">{row.tag ?? row.role}</span>}
                              {row.resolved && <span className="font-normal text-[#718174]" data-id="element-257" data-mp="3sdhah0e55y90">
                                  <span className="text-[#a6b0a7]" data-id="element-258" data-mp="3nffs6hoji2yl">→</span> {row.resolved}
                                </span>}
                              {row.amount && <span className="font-mono text-xs text-[#8b968d]" data-id="element-259" data-mp="oycxp1t1u3g5">— {row.amount}</span>}
                            </p>
                            <p className="mt-1 text-sm text-[#617366]" data-id="element-260" data-mp="f6798220a45p">{foodStandardLine}</p>
                            {row.source && <p className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-[#8b968d]" data-id="element-261" data-mp="a8e0b19biw1q">Source: <RegulationLink text={row.source} data-id="element-262" data-mp="17njkp10vlh7r" /></p>}
                          </div>
                          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-forest-green/10 px-2.5 py-1 text-xs font-semibold text-forest-green" data-id="element-263" data-mp="12vrp2c2v4lar">
                            <Check className="h-3.5 w-3.5" data-id="element-264" data-mp="1b7a95yhk94tz" />
                            OK — food ingredient
                          </span>
                        </li>)}
                  </ul>}
              </div>)}
          </div>
        </div>
      </section>
    </div>;
}